import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { format, endOfMonth } from 'date-fns';
import { es } from 'date-fns/locale';
import { sendOdooBilling } from '../utils/api';

const CARD_STYLE = {
  background: '#fff',
  border: '1px solid #E2E5EC',
  borderRadius: 10,
  padding: '18px 20px',
};

const LABEL_STYLE = {
  fontSize: '0.6875rem',
  fontWeight: 600,
  color: '#6B7A99',
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
  margin: '0 0 6px',
};

const CONTROL_STYLE = {
  padding: '7px 12px',
  borderRadius: 6,
  border: '1px solid #E2E5EC',
  background: '#fff',
  color: '#021344',
  fontSize: '0.8125rem',
  outline: 'none',
  fontFamily: 'inherit',
};

const buttonStyle = (primary, disabled) => ({
  ...CONTROL_STYLE,
  background: primary ? '#1F50FF' : '#fff',
  color: primary ? '#fff' : '#021344',
  border: primary ? '1px solid #1F50FF' : '1px solid #E2E5EC',
  fontWeight: 500,
  cursor: disabled ? 'default' : 'pointer',
  opacity: disabled ? 0.6 : 1,
});

const TRIGGER_LABELS = { cron: 'Automático', manual: 'Manual' };

/**
 * Formats an ISO date as "30 nov 2026, 18:00".
 * @param {string} iso
 * @returns {string}
 */
function formatDateTime(iso) {
  return format(new Date(iso), "d MMM yyyy, HH:mm", { locale: es });
}

/**
 * Green/red pill for a call result.
 */
function ResultBadge({ ok }) {
  return (
    <span style={{
      fontSize: '0.75rem',
      fontWeight: 600,
      padding: '2px 10px',
      borderRadius: 9999,
      background: ok ? '#DCFCE7' : '#FEE2E2',
      color: ok ? '#15803D' : '#B91C1C',
    }}>
      {ok ? '✓ OK' : '✗ Error'}
    </span>
  );
}

ResultBadge.propTypes = { ok: PropTypes.bool.isRequired };

/**
 * Small amber "PRUEBA" tag for test sends.
 */
function TestTag() {
  return (
    <span style={{
      fontSize: '0.6875rem', fontWeight: 600, padding: '1px 8px', borderRadius: 9999,
      background: '#FEF3C7', color: '#B45309',
    }}>
      PRUEBA
    </span>
  );
}

const entryShape = PropTypes.shape({
  sent_at: PropTypes.string.isRequired,
  trigger: PropTypes.string,
  month: PropTypes.string,
  attempt: PropTypes.number,
  params: PropTypes.shape({
    mes: PropTypes.string,
    pj: PropTypes.number,
    prelco: PropTypes.number,
    prueba: PropTypes.string,
  }),
  ok: PropTypes.bool.isRequired,
  http_status: PropTypes.number,
  response: PropTypes.string,
  error: PropTypes.string,
});

/**
 * Card with the last webhook call: what was sent and what Odoo answered.
 */
function LastSend({ entry }) {
  if (!entry) {
    return (
      <div style={{ ...CARD_STYLE, color: '#6B7A99', fontSize: '0.875rem' }}>
        Todavía no se ha enviado nada a Odoo.
      </div>
    );
  }
  const { params = {} } = entry;
  return (
    <div style={{ ...CARD_STYLE, borderColor: entry.ok ? '#BBF7D0' : '#FECACA' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 16 }}>
        <h3 style={{ margin: 0, fontSize: '0.95rem', fontWeight: 600, color: '#021344' }}>Último envío</h3>
        <ResultBadge ok={entry.ok} />
        {params.prueba && <TestTag />}
        <span style={{ fontSize: '0.8125rem', color: '#6B7A99' }}>
          {formatDateTime(entry.sent_at)} · {TRIGGER_LABELS[entry.trigger] || entry.trigger}
          {entry.attempt > 1 ? ` · intento ${entry.attempt}` : ''}
        </span>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 20 }}>
        <div>
          <p style={LABEL_STYLE}>Datos enviados</p>
          <table style={{ fontSize: '0.875rem', color: '#021344', borderCollapse: 'collapse' }}>
            <tbody>
              <tr><td style={{ padding: '2px 16px 2px 0', color: '#6B7A99' }}>mes</td><td>{params.mes}</td></tr>
              <tr><td style={{ padding: '2px 16px 2px 0', color: '#6B7A99' }}>pj</td><td>{params.pj ?? '—'}</td></tr>
              <tr><td style={{ padding: '2px 16px 2px 0', color: '#6B7A99' }}>prelco</td><td>{params.prelco ?? '—'}</td></tr>
              {params.prueba && (
                <tr><td style={{ padding: '2px 16px 2px 0', color: '#6B7A99' }}>prueba</td><td>{params.prueba}</td></tr>
              )}
            </tbody>
          </table>
        </div>
        <div>
          <p style={LABEL_STYLE}>
            Respuesta de Odoo{entry.http_status ? ` · HTTP ${entry.http_status}` : ''}
          </p>
          {entry.response && (
            <pre style={{
              margin: 0, padding: '8px 10px', borderRadius: 6, background: '#F8F9FC',
              border: '1px solid #F0F2F8', fontSize: '0.8125rem', color: '#021344',
              whiteSpace: 'pre-wrap', wordBreak: 'break-word', maxHeight: 160, overflow: 'auto',
            }}>
              {entry.response}
            </pre>
          )}
          {entry.error && (
            <p style={{ margin: entry.response ? '8px 0 0' : 0, fontSize: '0.8125rem', color: '#B91C1C' }}>
              {entry.error}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

LastSend.propTypes = { entry: entryShape };

/**
 * Month picker + preview / send buttons.
 */
function ManualSend({ defaultMonth, onSent }) {
  const [month, setMonth] = useState(defaultMonth);
  const [test, setTest] = useState(true);
  const [busy, setBusy] = useState(null); // null | 'preview' | 'send'
  const [result, setResult] = useState(null); // { kind: 'preview'|'sent'|'error', text }

  const run = async (dryRun) => {
    if (!dryRun && !test) {
      const ok = window.confirm(
        `Se enviará el resumen REAL de ${month} a Odoo y llegará por correo a Jose Ignacio, Esteban, Administración e Isabel. ¿Continuar?`,
      );
      if (!ok) return;
    }
    setBusy(dryRun ? 'preview' : 'send');
    setResult(null);
    try {
      const r = await sendOdooBilling({ month, test, dryRun });
      setResult({
        kind: dryRun ? 'preview' : 'sent',
        text: `${r.mes}: P&J ${r.pj} remates · Prelco ${r.prelco} remates`,
      });
    } catch (err) {
      setResult({ kind: 'error', text: err.message });
    } finally {
      setBusy(null);
      if (!dryRun) onSent();
    }
  };

  const resultColor = { preview: '#021344', sent: '#15803D', error: '#B91C1C' };
  const resultPrefix = { preview: 'Vista previa (no enviado) — ', sent: '✓ Enviado — ', error: '✗ ' };

  return (
    <div style={CARD_STYLE}>
      <h3 style={{ margin: '0 0 14px', fontSize: '0.95rem', fontWeight: 600, color: '#021344' }}>Envío manual</h3>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <input
          type="month"
          value={month}
          max={defaultMonth}
          onChange={e => setMonth(e.target.value)}
          style={{ ...CONTROL_STYLE, cursor: 'pointer' }}
        />
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.8125rem', color: '#021344', cursor: 'pointer' }}>
          <input type="checkbox" checked={test} onChange={e => setTest(e.target.checked)} />
          Prueba (asunto con [PRUEBA])
        </label>
        <button onClick={() => run(true)} disabled={!!busy || !month} style={buttonStyle(false, !!busy)}>
          {busy === 'preview' ? 'Calculando…' : 'Vista previa'}
        </button>
        <button onClick={() => run(false)} disabled={!!busy || !month} style={buttonStyle(true, !!busy)}>
          {busy === 'send' ? 'Enviando…' : test ? 'Enviar prueba a Odoo' : 'Enviar a Odoo'}
        </button>
      </div>
      {result && (
        <p style={{ margin: '12px 0 0', fontSize: '0.875rem', color: resultColor[result.kind] }}>
          {resultPrefix[result.kind]}{result.text}
        </p>
      )}
    </div>
  );
}

ManualSend.propTypes = {
  defaultMonth: PropTypes.string.isRequired,
  onSent: PropTypes.func.isRequired,
};

/**
 * Table of previous webhook calls.
 */
function History({ entries }) {
  if (entries.length < 2) return null;
  const th = { padding: '8px 12px', textAlign: 'left', fontSize: '0.75rem', fontWeight: 600, color: '#6B7A99', borderBottom: '1px solid #E2E5EC' };
  const td = { padding: '8px 12px', fontSize: '0.8125rem', color: '#021344', borderBottom: '1px solid #F0F2F8' };
  return (
    <div style={{ ...CARD_STYLE, padding: 0, overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr style={{ background: '#F8F9FC' }}>
            <th style={th}>Fecha</th>
            <th style={th}>Origen</th>
            <th style={th}>Mes</th>
            <th style={th}>P&amp;J</th>
            <th style={th}>Prelco</th>
            <th style={th}>Resultado</th>
            <th style={th}>Respuesta</th>
          </tr>
        </thead>
        <tbody>
          {entries.map(e => (
            <tr key={`${e.sent_at}-${e.attempt}`}>
              <td style={{ ...td, whiteSpace: 'nowrap' }}>{formatDateTime(e.sent_at)}</td>
              <td style={td}>
                {TRIGGER_LABELS[e.trigger] || e.trigger}{e.attempt > 1 ? ` (intento ${e.attempt})` : ''}
              </td>
              <td style={{ ...td, whiteSpace: 'nowrap' }}>
                {e.params?.mes} {e.params?.prueba && <TestTag />}
              </td>
              <td style={td}>{e.params?.pj ?? '—'}</td>
              <td style={td}>{e.params?.prelco ?? '—'}</td>
              <td style={td}><ResultBadge ok={e.ok} /></td>
              <td style={{ ...td, color: e.ok ? '#6B7A99' : '#B91C1C', maxWidth: 420, wordBreak: 'break-word' }}>
                {e.http_status ? `HTTP ${e.http_status} · ` : ''}{e.ok ? e.response : (e.error || e.response)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

History.propTypes = { entries: PropTypes.arrayOf(entryShape).isRequired };

/**
 * Odoo billing view: last webhook call, manual send and history.
 */
function OdooBilling({ entries, config, onRefresh }) {
  const defaultMonth = config?.current_month || format(new Date(), 'yyyy-MM');
  const nextRun = format(endOfMonth(new Date()), "EEEE d 'de' MMMM, '18:00'", { locale: es });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {config && !config.configured && (
        <div style={{ ...CARD_STYLE, background: '#FEF2F2', borderColor: '#FECACA', color: '#B91C1C', fontSize: '0.875rem' }}>
          Falta configurar ODOO_BILLING_WEBHOOK_URL en el backend: no se puede enviar nada.
        </div>
      )}
      <p style={{ margin: 0, fontSize: '0.8125rem', color: '#6B7A99' }}>
        Envío automático: {config?.schedule || 'último día de cada mes, 18:00'} · próximo: {nextRun}
        {config?.db_label ? ` · base de datos: ${config.db_label}` : ''}
      </p>
      <LastSend entry={entries[0]} />
      <ManualSend defaultMonth={defaultMonth} onSent={onRefresh} />
      <History entries={entries} />
    </div>
  );
}

OdooBilling.propTypes = {
  entries: PropTypes.arrayOf(entryShape).isRequired,
  config: PropTypes.shape({
    db: PropTypes.number,
    db_label: PropTypes.string,
    configured: PropTypes.bool,
    current_month: PropTypes.string,
    schedule: PropTypes.string,
  }),
  onRefresh: PropTypes.func.isRequired,
};

export default OdooBilling;
